import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, CheckCircle2, ChevronRight, Play
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSeo from '../components/PageSeo';
import { getUserDashboard, hasActiveSubscription } from '../services/authApi';

const HERO_WORDS = ['Your Future.', 'Financial Freedom.', 'Wealth Building.'];

const FEATURED_COURSES = [
  {
    title: 'Why Go For High Paying Clients',
    description: 'Explore the value of working with high-paying clients and how a focus on premium services can support your business growth.',
  },
  {
    title: 'What It Takes to Close High Paying Clients',
    description: 'Learn how to communicate your value, build trust, and approach sales conversations with prospective high-paying clients.',
  },
  {
    title: 'How to Position Yourself as an Expert',
    description: 'Discover how to highlight your expertise, strengthen your credibility, and help the right clients understand what sets you apart.',
  },
  {
    title: 'How to Identify and Qualify Clients',
    description: 'Learn how to evaluate potential clients, understand their needs, and identify opportunities that are a good fit for your services.',
  },
];

const RESPONSIVE_IMAGE_WIDTHS = {
  about: [480, 768, 960],
  accounting: [480, 768, 1200],
  affiliate: [480, 768, 960],
  cryptoBlock: [480, 768, 1200],
  homeBanner: [960, 1600, 2400],
  step1: [320, 640, 960],
};

const buildSrcSet = (name, widths, extension) => (
  widths.map((width) => `/optimized/${name}-${width}.${extension} ${width}w`).join(', ')
);

function ResponsiveImage({
  alt,
  className,
  decoding = 'async',
  fallbackName,
  fallbackWidth,
  fetchPriority = 'auto',
  height,
  loading = 'lazy',
  sizes,
  width,
  widths,
}) {
  return (
    <picture>
      <source
        type="image/webp"
        srcSet={buildSrcSet(fallbackName, widths, 'webp')}
        sizes={sizes}
      />
      <source
        type="image/jpeg"
        srcSet={buildSrcSet(fallbackName, widths, 'jpg')}
        sizes={sizes}
      />
      <img
        src={`/optimized/${fallbackName}-${fallbackWidth}.jpg`}
        alt={alt}
        className={className}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
      />
    </picture>
  );
}

export default function PersonalFinanceAcademy() {
  const [displayedText, setDisplayedText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasCourseAccess, setHasCourseAccess] = useState(false);
  const courseTarget = hasCourseAccess ? '/library/' : '/pricing/';

  useEffect(() => {
    let cancelled = false;
    const checkCourseAccess = async () => {
      if (!localStorage.getItem('authToken')) {
        localStorage.removeItem('paymentSubmitted');
        setHasCourseAccess(false);
        return;
      }

      try {
        const dashboard = await getUserDashboard();
        if (cancelled) return;
        const subscription = dashboard?.subscriptions?.[0];
        const hasAccess = subscription
          ? hasActiveSubscription({ plan_id: subscription.plan_id, subscription_status: subscription.status })
          : hasActiveSubscription(dashboard);

        setHasCourseAccess(hasAccess);

        if (hasAccess) {
          localStorage.setItem('paymentSubmitted', 'true');
        } else {
          localStorage.removeItem('paymentSubmitted');
        }
      } catch (error) {
        if (!cancelled) setHasCourseAccess(false);
      }
    };

    checkCourseAccess();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fullWord = HERO_WORDS[wordIndex];
    let timeout;
    const isLastWord = wordIndex === HERO_WORDS.length - 1;

    if (!isDeleting && displayedText === fullWord) {
      // Stop once the final word is fully typed.
      if (isLastWord) return;
      timeout = setTimeout(() => setIsDeleting(true), 1600);
    } else if (isDeleting && displayedText === '') {
      setIsDeleting(false);
      setWordIndex((prev) => (prev + 1) % HERO_WORDS.length);
    } else if (isDeleting) {
      timeout = setTimeout(() => setDisplayedText((prev) => prev.slice(0, -1)), 45);
    } else {
      timeout = setTimeout(() => setDisplayedText(fullWord.slice(0, displayedText.length + 1)), 90);
    }

    return () => clearTimeout(timeout);
  }, [displayedText, isDeleting, wordIndex]);

  return (
    <div className="min-h-screen font-sans bg-[#0F172A] text-white">
      <PageSeo
        title="Master Wealth Building & Financial Freedom Online"
        description="Learn budgeting, investing, and wealth-building strategies with expert guidance to achieve lasting financial freedom and confidence today. Start today. Go!"
        canonicalUrl="https://personalfinanceacademy.net/"
        ogTitle="Personal Finance Academy | Build Financial Freedom Through Education"
        ogDescription="Learn practical money skills, budgeting, investing, and wealth-building strategies to take control of your financial future with Personal Finance Academy."
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://personalfinanceacademy.net/#organization",
              name: "Personal Finance Academy",
              url: "https://personalfinanceacademy.net/",
              contactPoint: {
                "@type": "ContactPoint",
                contactType: "customer support",
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://personalfinanceacademy.net/#website",
              url: "https://personalfinanceacademy.net/",
              name: "Personal Finance Academy",
              publisher: {
                "@id": "https://personalfinanceacademy.net/#organization",
              },
            },
            {
              "@type": "WebPage",
              "@id": "https://personalfinanceacademy.net/#webpage",
              url: "https://personalfinanceacademy.net/",
              name: "Personal Finance Academy",
              description:
                "Personal Finance Academy provides educational resources and learning content focused on personal finance, money management, budgeting, saving, and financial literacy.",
              isPartOf: {
                "@id": "https://personalfinanceacademy.net/#website",
              },
              about: {
                "@id": "https://personalfinanceacademy.net/#finance-learning-service",
              },
            },
            {
              "@type": "Service",
              "@id": "https://personalfinanceacademy.net/#finance-learning-service",
              name: "Personal Finance Academy Educational Service",
              serviceType: "Personal finance educational service",
              description:
                "An online educational service providing resources and learning content focused on personal finance, money management, budgeting, saving, and financial literacy.",
              provider: {
                "@id": "https://personalfinanceacademy.net/#organization",
              },
              url: "https://personalfinanceacademy.net/",
            },
          ],
        }}
      />

      {/* Navigation */}
      <div className="relative z-50">
        <Header />
      </div>

      {/* Main Content Wrapper */}
      <main className="mx-auto overflow-hidden relative">
        <div className="sr-only">
          <h2>Master the Art of Wealth Building.</h2>
          <h2>Real skills, financial freedom.</h2>
          <h3>Practical, real-world investment strategies</h3>
          <h3>Deep dives into Crypto, Stocks &amp; Accounting</h3>
          <h3>Expert-led market analysis &amp; passive income generation</h3>
          <h2>Expert Skills for Every Investor</h2>
          <h2>Take Control of Your Finances</h2>
          <h2>Build Income From Anywhere</h2>
          <h2>Turn Recommendations Into Revenue</h2>
          <h2>Invest Smart in Digital Assets</h2>
          <h2>Your Path to Success Mastery</h2>
          <h3>Create your account</h3>
          <h3>Dive into Courses</h3>
          <h3>Download &amp; Apply</h3>
          <h2>Master Your Personal Finances And Build Lasting Wealth</h2>
        </div>



      {/* ========================================= */}
      {/* HERO SECTION (MARS EXPLORER LAYOUT)       */}
      {/* ========================================= */}
      <section className="relative w-full">
        {/* Main Floating Card Container */}
        <div className="relative overflow-hidden shadow-2xl bg-[#0F172A]">
          
          {/* Background Image Area */}
          <div className="relative lg:h-[75vh] h-[600px] lg:max-h-[73vh] max-h-[52vh] w-full">
            <ResponsiveImage
              fallbackName="home-banner"
              fallbackWidth={1600}
              widths={RESPONSIVE_IMAGE_WIDTHS.homeBanner}
              alt="Wealth Background"
              width={4000}
              height={2261}
              sizes="100vw"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              className="absolute inset-0 w-full h-full object-cover opacity-50"
            />
            {/* Gradient Overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent" />
            
            {/* Central Headline within the card */}
            <div className="max-w-[1600px] mx-auto absolute inset-0 flex flex-col justify-center px-4 md:px-16 lg:px-24">
              <h1 className="text-[36px] md:text-7xl font-extrabold text-white leading-tight max-w-3xl">
                Master the Art of <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E] block min-h-[1.2em] max-w-[90vw] whitespace-normal break-words [overflow-wrap:anywhere]">
                  {displayedText || '\u00A0'}
                </span>
              </h1>
            </div>
          </div>

<div className=" lg:min-h-[30vh]">
          {/* The Curved Accent Wave */}
          <div className="relative -mt-20 z-20">
            <svg viewBox="0 0 1440 120" className="w-full h-full max-h-[135px] fill-[#0F172A]">
              <path d="M0,64L120,58.7C240,53,420,43,640,48C860,53,1080,75,1320,80C1560,85,1760,75,1860,69.3L1920,64V120H0Z"></path>
            </svg>
            <div className="absolute top-0 w-full h-2 bg-gradient-to-r from-blue-500 to-rose-500 w-full" />
          </div>

          {/* Quote & Original Button Design Section */}
          <div className="max-w-[1600px] mx-auto px-4 pb-4  md:px-16 lg:px-24 pb-20 justify-between lg:flex-row gap-12 lg:items-center flex flex-col-reverse items-baseline">
            <p className="max-w-[850px] text-lg text-slate-300 leading-relaxed">
              "Stop guessing with your money. Learn personal finance, cryptocurrency, accounting, and passive income strategies to secure your financial future. It's about believing in the future and thinking that the future will be better than the past."
            </p>
            
            {/* Restored Original Button Design */}
            <div className="flex flex-col sm:flex-row lg:items-center justify-center lg:justify-start gap-5 items-baseline">
              <Link 
                to={courseTarget}
                className=" flex whitespace-nowrap w-full sm:w-auto relative group overflow-hidden bg-[#2563EB] text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-500 transition-all duration-300 shadow-[0_0_40px_-10px_rgba(37,99,235,0.7)] text-lg flex gap-2 items-center justify-center hover:-translate-y-1"
              >
                Explore Courses <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link 
                to="/pricing/" 
                className="flex whitespace-nowrap w-full sm:w-auto bg-slate-800/40 backdrop-blur-md border border-slate-700 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 hover:border-[#F43F5E]/50 hover:text-white transition-all duration-300 shadow-lg hover:-translate-y-1 inline-flex items-center justify-center text-lg gap-3 group"
              >
                <div className="bg-[#F43F5E]/20 p-1.5 rounded-xl group-hover:bg-[#F43F5E]/30 transition-colors">
                  <Play className="w-4 h-4 text-[#F43F5E] group-hover:scale-110 transition-transform fill-current" /> 
                </div>
                View Pricing
              </Link>
            </div>
          </div>
          </div>
        </div>
      </section>


      {/* ========================================= */}
      {/* 2. ADVANTAGE SECTION                      */}
      {/* ========================================= */}
        <section className="py-20 sm:py-32 px-4 sm:px-8 w-full bg-white relative z-10 text-[#0F172A] border-y border-slate-200">
          <div className="max-w-7xl lg:px-6 mx-auto flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-20">
            {/* Image Side */}
            <div className="relative rounded-3xl border border-slate-200 overflow-hidden group shadow-xl h-64 sm:h-96 lg:h-[480px] w-full bg-slate-50">
              <ResponsiveImage
                fallbackName="about"
                fallbackWidth={768}
                widths={RESPONSIVE_IMAGE_WIDTHS.about}
                alt="Financial Growth Charts"
                width={736}
                height={1288}
                sizes="(min-width: 1024px) 50vw, 100vw"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>

            {/* Text Side */}
            <div className="order-1 text-left lg:order-2 w-full">
              <h2 className="text-sm font-bold tracking-widest text-[#2563EB] uppercase mb-3">Our Advantage</h2>
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-[#0F172A]">Real skills,<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">financial freedom.</span></h3>
              <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                Building wealth requires a modern approach. We teach you how to leverage investing platforms, understand digital markets, and build a diversified portfolio that compounds over time.
              </p>
              <ul className="space-y-4">
                {[
                  { text: "Practical, real-world investment strategies" },
                  { text: "Deep dives into Crypto, Stocks & Accounting" },
                  { text: "Expert-led market analysis & passive income generation" }
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-4 text-[#0F172A] bg-slate-50 p-4 rounded-xl border border-slate-200 hover:border-[#2563EB]/30 shadow-sm transition-colors">
                    <CheckCircle2 className="w-6 h-6 text-[#2563EB] shrink-0" />
                    <span className="font-medium text-lg">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* 3. CURRICULUM SECTION (BOX-FREE DESIGN)   */}
        {/* ========================================= */}
        <section className="py-24 sm:py-32 px-4 md:px-8 w-full bg-[#0F172A] text-white relative z-10 overflow-hidden border-y border-slate-800">
          <div className="absolute inset-0 pointer-events-none opacity-40 [background-image:radial-gradient(circle_at_top_left,rgba(37,99,235,0.18),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(244,63,94,0.15),transparent_30%)]" />

          <div className="max-w-7xl lg:px-6 mx-auto relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-20 md:mb-28">
              <h3 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">Expert Skills for Every <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">Investor</span></h3>
              <p className="text-slate-400 text-base md:text-lg">
                Comprehensive modules designed to take you from foundational budgeting to advanced cryptocurrency and passive income strategies.
              </p>
            </div>

            <div className="flex flex-col gap-24 md:gap-32">

              {/* Row 1: Accounting */}
              <div className="flex flex-col md:flex-row items-center gap-10 lg:gap-0 relative w-full group">
                <div className="w-full md:w-1/2 relative z-10 shrink-0 md:pr-12 lg:pr-20">
                  <div className="rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/3] border border-slate-800 bg-slate-900">
                    <ResponsiveImage
                      fallbackName="accounting"
                      fallbackWidth={768}
                      widths={RESPONSIVE_IMAGE_WIDTHS.accounting}
                      alt="Accounting"
                      width={2000}
                      height={1333}
                      sizes="(min-width: 768px) 50vw, 100vw"
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                </div>
                
                {/* Text Content with Left Border Divider */}
                <div className="w-full md:w-1/2 relative pt-8 md:pt-4 border-t md:border-t-0 border-slate-800 md:border-l md:pl-12 lg:pl-20">
                  
                  {/* High-End Minimal Number */}
                  <div className="absolute -top-16 sm:-top-20 md:-top-24 -left-4 sm:-left-8 md:left-6 lg:left-14 text-[160px] sm:text-[200px] md:text-[240px] leading-none font-black tracking-tighter select-none pointer-events-none z-0 text-transparent bg-clip-text bg-gradient-to-b from-[#2563EB]/15 to-transparent">
                    01
                  </div>

                  <div className="relative z-10">
                    <h4 className="text-3xl lg:text-4xl font-bold mb-6 text-white leading-tight">Take Control of Your Finances</h4>
                    <p className="text-slate-400 text-base lg:text-lg leading-relaxed mb-8">
                      Gain the ability to read financial statements with confidence, track every dollar of cash flow, and stay on top of your taxes — all while building a scalable budget that works for both personal finances and growing businesses.
                    </p>
                    <Link to={courseTarget} className="w-fit py-3.5 px-8 rounded-full font-bold text-sm bg-[#2563EB] text-white shadow-[0_4px_14px_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.23)] hover:bg-blue-600 transition-all duration-300 flex items-center justify-center gap-2 group">
                      Explore Module <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Row 2: Work From Home */}
              <div className="flex flex-col md:flex-row-reverse items-center gap-10 lg:gap-0 relative w-full group">
                <div className="w-full md:w-1/2 relative z-10 shrink-0 md:pl-12 lg:pl-20">
                  <div className="rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/3] border border-slate-800 bg-slate-900">
                    <img
                      src="https://images.unsplash.com/photo-1587560699334-bea93391dcef?auto=format&fit=crop&q=76&w=900"
                      srcSet="https://images.unsplash.com/photo-1587560699334-bea93391dcef?auto=format&fit=crop&q=76&w=600 600w, https://images.unsplash.com/photo-1587560699334-bea93391dcef?auto=format&fit=crop&q=76&w=900 900w, https://images.unsplash.com/photo-1587560699334-bea93391dcef?auto=format&fit=crop&q=76&w=1200 1200w"
                      sizes="(min-width: 768px) 50vw, 100vw"
                      alt="Work from home"
                      width={889}
                      height={667}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                </div>
                
                {/* Text Content with Right Border Divider */}
                <div className="w-full md:w-1/2 relative pt-8 md:pt-4 border-t md:border-t-0 border-slate-800 md:border-r md:pr-12 lg:pr-20 text-left">
                  
                  {/* High-End Minimal Number */}
                  <div className="absolute -top-16 sm:-top-20 md:-top-24 right-auto left-0 md:left-auto md:-right-4 lg:-right-8 text-[160px] sm:text-[200px] md:text-[240px] leading-none font-black tracking-tighter select-none pointer-events-none z-0 text-transparent bg-clip-text bg-gradient-to-b from-[#F43F5E]/15 to-transparent">
                    02
                  </div>

                  <div className="relative z-10">
                    <h4 className="text-3xl lg:text-4xl font-bold mb-6 text-white leading-tight">Build Income From Anywhere</h4>
                    <p className="text-slate-400 text-base lg:text-lg leading-relaxed mb-8">
                      Unlock over 20 verified remote income streams, learn how to attract and retain high-paying freelance clients, and set up automated passive revenue systems that earn money around the clock — all from your laptop.
                    </p>
                    <Link to={courseTarget} className="w-fit py-3.5 px-8 rounded-full font-bold text-sm bg-[#2563EB] text-white shadow-[0_4px_14px_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.23)] hover:bg-blue-600 transition-all duration-300 flex items-center justify-center gap-2 group">
                      Explore Module <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Row 3: Affiliate Marketing */}
              <div className="flex flex-col md:flex-row items-center gap-10 lg:gap-0 relative w-full group">
                <div className="w-full md:w-1/2 relative z-10 shrink-0 md:pr-12 lg:pr-20">
                  <div className="rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/3] border border-slate-800 bg-slate-900">
                    <ResponsiveImage
                      fallbackName="affiliate"
                      fallbackWidth={768}
                      widths={RESPONSIVE_IMAGE_WIDTHS.affiliate}
                      alt="Affiliate Marketing"
                      width={736}
                      height={1313}
                      sizes="(min-width: 768px) 50vw, 100vw"
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                </div>
                
                {/* Text Content with Left Border Divider */}
                <div className="w-full md:w-1/2 relative pt-8 md:pt-4 border-t md:border-t-0 border-slate-800 md:border-l md:pl-12 lg:pl-20">
                  
                  {/* High-End Minimal Number */}
                  <div className="absolute -top-16 sm:-top-20 md:-top-24 -left-4 sm:-left-8 md:left-6 lg:left-14 text-[160px] sm:text-[200px] md:text-[240px] leading-none font-black tracking-tighter select-none pointer-events-none z-0 text-transparent bg-clip-text bg-gradient-to-b from-indigo-500/15 to-transparent">
                    03
                  </div>

                  <div className="relative z-10">
                    <h4 className="text-3xl lg:text-4xl font-bold mb-6 text-white leading-tight">Turn Recommendations Into Revenue</h4>
                    <p className="text-slate-400 text-base lg:text-lg leading-relaxed mb-8">
                      Learn to identify and promote high-converting products in any niche, build sales funnels that convert, drive consistent organic and paid traffic, and collect recurring commissions from audiences around the world.
                    </p>
                    <Link to={courseTarget} className="w-fit py-3.5 px-8 rounded-full font-bold text-sm bg-[#2563EB] text-white shadow-[0_4px_14px_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.23)] hover:bg-blue-600 transition-all duration-300 flex items-center justify-center gap-2 group">
                      Explore Module <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Row 4: Cryptocurrency */}
              <div className="flex flex-col md:flex-row-reverse items-center gap-10 lg:gap-0 relative w-full group">
                <div className="w-full md:w-1/2 relative z-10 shrink-0 md:pl-12 lg:pl-20">
                  <div className="rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/3] border border-slate-800 bg-slate-900">
                    <ResponsiveImage
                      fallbackName="crypto-block"
                      fallbackWidth={768}
                      widths={RESPONSIVE_IMAGE_WIDTHS.cryptoBlock}
                      alt="Crypto"
                      width={5926}
                      height={3951}
                      sizes="(min-width: 768px) 50vw, 100vw"
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                </div>
                
                {/* Text Content with Right Border Divider */}
                <div className="w-full md:w-1/2 relative pt-8 md:pt-4 border-t md:border-t-0 border-slate-800 md:border-r md:pr-12 lg:pr-20 text-left">
                  
                  {/* High-End Minimal Number */}
                  <div className="absolute -top-16 sm:-top-20 md:-top-24 right-auto left-0 md:left-auto md:-right-4 lg:-right-8 text-[160px] sm:text-[200px] md:text-[240px] leading-none font-black tracking-tighter select-none pointer-events-none z-0 text-transparent bg-clip-text bg-gradient-to-b from-[#2563EB]/15 to-transparent">
                    04
                  </div>

                  <div className="relative z-10">
                    <h4 className="text-3xl lg:text-4xl font-bold mb-6 text-white leading-tight">Invest Smart in Digital Assets</h4>
                    <p className="text-slate-400 text-base lg:text-lg leading-relaxed mb-8">
                      Understand how blockchain technology works, evaluate tokenomics to spot winning projects early, safely set up wallets and exchanges, and execute confident trades using proven strategies that protect and grow your portfolio.
                    </p>
                    <Link to={courseTarget} className="w-fit py-3.5 px-8 rounded-full font-bold text-sm bg-[#2563EB] text-white shadow-[0_4px_14px_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.23)] hover:bg-blue-600 transition-all duration-300 flex items-center justify-center gap-2 group">
                      Explore Module <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* FEATURED COURSE LIBRARY                    */}
        {/* ========================================= */}
        <section aria-labelledby="featured-courses-heading" className="py-24 sm:py-32 px-4 sm:px-8 w-full bg-white relative z-10 text-[#0F172A] border-b border-slate-200">
          <div className="max-w-7xl lg:px-6 mx-auto">
            <div className="max-w-3xl mb-12 md:mb-16">
              <p className="text-sm font-bold tracking-widest text-[#2563EB] uppercase mb-3">Course Library</p>
              <h2 id="featured-courses-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-5">
                Explore Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">Courses</span>
              </h2>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                Start with these four lessons to explore how to attract premium clients, communicate your expertise, and find the right fit for your business.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {FEATURED_COURSES.map((course) => (
                <article key={course.title} className="flex flex-col rounded-3xl border border-slate-200 bg-slate-50 p-6 sm:p-8 transition-all duration-300 hover:border-blue-300 hover:shadow-xl">
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="rounded-xl bg-blue-100 p-3 text-[#2563EB]">
                      <BookOpen className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <span className="rounded-full bg-blue-100 px-3 py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-blue-700">Course Library</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold leading-tight mb-3">{course.title}</h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6">{course.description}</p>
                  <div className="mt-auto border-t border-slate-200 pt-5">
                    <div className="flex flex-wrap gap-2 mb-5 text-xs font-medium text-slate-600">
                      <span className="rounded-md bg-white border border-slate-200 px-2.5 py-1">Video lesson</span>
                    </div>
                    <Link to={courseTarget} aria-label={`Explore ${course.title} in the course library`} className="group inline-flex items-center gap-2 rounded-md text-sm font-bold text-[#2563EB] hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600">
                      Explore Course Library
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* 4. PROCESS SECTION                        */}
        {/* ========================================= */}
        <section className="py-24 sm:py-32 px-4 sm:px-8 w-full bg-slate-50 relative z-10 text-[#0F172A]">
          <div className="max-w-7xl lg:px-6 mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-sm font-bold tracking-widest text-[#2563EB] uppercase mb-3">The Process</h2>
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-[#0F172A]">Your Path to Success <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">Mastery</span></h3>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Follow these three simple steps to start building your financial literacy and expanding your wealth.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10 items-start pb-24">
              {[
                { step: "1", title: "Create your account", desc: "Sign up in seconds. Get instant access to your personalized learning dashboard.", img: "/step1.jpg", offset: "mt-0" },
                { step: "2", title: "Dive into Courses", desc: "Follow structured learning paths curated by industry-leading financial experts.", img: "https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&q=80&w=600", offset: "md:mt-12" },
                { step: "3", title: "Download & Apply", desc: "Get instant access to our expert-built templates, cheat sheets, and investment trackers. Download your resources and put your knowledge to work immediately.", img: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&q=80&w=600", offset: "md:mt-24" }
              ].map((item, i) => (
                <div key={i} className={`bg-white rounded-[2rem] border border-slate-200 flex flex-col group overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300 ${item.offset}`}>
                  <div className="relative w-full h-56 sm:h-64 overflow-hidden">
                    {item.img === '/step1.jpg' ? (
                      <ResponsiveImage
                        fallbackName="step1"
                        fallbackWidth={640}
                        widths={RESPONSIVE_IMAGE_WIDTHS.step1}
                        alt={item.title}
                        width={736}
                        height={490}
                        sizes="(min-width: 768px) 33vw, 100vw"
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      />
                    ) : (
                      <img
                        src={item.img}
                        alt={item.title}
                        width={600}
                        height={400}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      />
                    )}
                    <div className="absolute top-4 left-4 bg-white w-10 h-10 rounded-xl flex items-center justify-center font-black text-xl text-[#F43F5E] shadow-md border border-slate-100">
                      {item.step}
                    </div>
                  </div>
                  <div className="p-8 flex flex-col">
                    <h4 className="text-xl font-bold mb-3 text-[#0F172A]">{item.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer (Dark) */}
        <div className="bg-[#0F172A] relative">
          <Footer />
        </div>

      </main>
    </div>
  );
}
